import React from 'react';
import { Input } from 'src/design/atoms/Input';
import { Select } from 'src/design/atoms/Select';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { TextInput } from 'src/design/atoms/TextInput';
import { JobUserSelect } from 'src/app/selectors/JobUserSelectSingle';
import { ContactPicker } from 'src/app/selectors/ContactPicker';
import { MilestonePhaseItemSelector } from 'src/app/selectors/MilestonePhaseItemSelector';
import { IContact } from 'src/api/models/Organization';
import { FIELD_LIMITS } from 'src/utils';

type MilestoneValue = React.ComponentProps<
  typeof MilestonePhaseItemSelector
>['value'];

/** Messages to surface under the fields this block owns, keyed by DTO field name. */
export type WorkflowRecordFieldErrors = Partial<
  Record<
    | 'title'
    | 'assigneeUserId'
    | 'company'
    | 'intentOfReview'
    | 'milestonePhaseItemId',
    React.ReactNode
  >
>;

export interface IWorkflowRecordFieldsProps {
  title: string | undefined;
  assigneeUserId: number | undefined;
  company: string | undefined;
  intentOfReview: string | undefined;
  milestonePhaseItemId: string | undefined;
  /** Read-only mode for a persisted record a non-admin opened. */
  disabled: boolean;
  jobId: number;
  customerNumber: string;
  customerName: string;
  submittedToContact: IContact | null;
  onFieldChange: (field: string, value: unknown) => void;
  onSubmittedToChange: (contact: IContact | null) => void;
  fieldErrors?: WorkflowRecordFieldErrors;
}

/**
 * @registry The shared field block of a workflow record form (Submittal, Drawing): Title,
 * Assignee, Linked Milestone Item, Intent of Review, Company Name and the contact-only
 * "Submitted To" selector. Writes through onFieldChange with the DTO field names, so the
 * caller only has to hand it formik's setFieldValue.
 */
export const WorkflowRecordFields: React.FC<IWorkflowRecordFieldsProps> = ({
  title,
  assigneeUserId,
  company,
  intentOfReview,
  milestonePhaseItemId,
  disabled,
  jobId,
  customerNumber,
  customerName,
  submittedToContact,
  onFieldChange,
  onSubmittedToChange,
  fieldErrors,
}) => (
  <SimpleGrid cols={{ base: 1, md: 2 }}>
    <Stack>
      <TextInput
        label="Title"
        name="title"
        withAsterisk
        disabled={disabled}
        maxLength={FIELD_LIMITS.singleLine}
        error={fieldErrors?.title}
        value={disabled && !title ? 'N/A' : title ?? ''}
        onChange={(event) => onFieldChange('title', event.target.value)}
        placeholder="Enter title..."
      />
      <Input.Wrapper
        withAsterisk
        label="Assignee"
        error={fieldErrors?.assigneeUserId}
      >
        <JobUserSelect
          hideLabel
          small
          hideRole
          selectedUserId={assigneeUserId}
          onChange={(value) => {
            onFieldChange('assigneeUserId', value?.userId);
            onFieldChange('company', value?.user?.companyName ?? '');
          }}
          disabled={disabled}
        />
      </Input.Wrapper>
      <Input.Wrapper
        label="Linked Milestone Item"
        error={fieldErrors?.milestonePhaseItemId}
      >
        <MilestonePhaseItemSelector
          value={
            milestonePhaseItemId
              ? ({ id: milestonePhaseItemId } as NonNullable<MilestoneValue>)
              : null
          }
          onChange={(item) => onFieldChange('milestonePhaseItemId', item?.id)}
          jobId={jobId}
          disabled={disabled}
        />
      </Input.Wrapper>
    </Stack>
    <Stack>
      <Select
        label="Intent of Review"
        error={fieldErrors?.intentOfReview}
        value={intentOfReview ?? ''}
        onChange={(value) => onFieldChange('intentOfReview', value)}
        disabled={disabled}
        data={[
          { value: 'review', label: 'Issued for Review and Acceptance' },
          { value: 'info_only', label: 'Issued for Information Only' },
        ]}
      />
      <TextInput
        label="Company Name"
        withAsterisk
        name="company"
        disabled={disabled}
        maxLength={FIELD_LIMITS.singleLine}
        error={fieldErrors?.company}
        value={disabled && !company ? 'N/A' : company ?? ''}
        onChange={(event) => onFieldChange('company', event.target.value)}
        placeholder="What's the company name?"
      />
      <ContactPicker
        label="Submitted To"
        disabled={disabled}
        allowCreate
        customerNumber={customerNumber}
        customerName={customerName}
        initialValue={submittedToContact}
        onChange={(contact) => {
          onSubmittedToChange(contact);
          onFieldChange('submittedToContactId', contact?.id);
        }}
      />
    </Stack>
  </SimpleGrid>
);
